/**
 * Which columns hold the identity of a record, per form.
 *
 * Every IRIS form names the same four things differently: who is filing, who is being
 * reported on, what that person is called, and where they live. 1099-K calls the recipient a
 * payee, 1099-LTC a policyholder, 3921/3922 an employee, 1042-S numbers the boxes. This table
 * is what lets one checkRecords cover all eighteen forms.
 *
 * Each entry lists candidate column names in precedence order; the first non-empty one wins.
 */
export interface FormIdentityFields {
  filerTin: string[];
  recipientTin: string[];
  /** A person name is enough, a business name is enough, either satisfies "has a name". */
  personName: string[];
  businessName: string[];
  city: string[];
  state: string[];
  zip: string[];
  foreignCountry: string[];
  /** Columns that can carry the record's own tax year. */
  taxYear: string[];
}

const COMMON_YEAR = ["1099_YEAR", "1098_YEAR", "TAX_YEAR", "1042_YEAR", "YEAR"];

function recipientDefaults(overrides: Partial<FormIdentityFields> = {}): FormIdentityFields {
  return {
    filerTin: ["FILER_FEDERAL_ID_NUMBER"],
    recipientTin: ["RECIPIENT_ID_NUMBER"],
    personName: [
      "RECIPIENT_FIRST_NAME",
      "RECIPIENT_LAST_NAME",
      "RECIPIENT_NAME",
      "RECIPIENT_NAME1",
      "RECIPIENT_NAME2",
    ],
    businessName: ["RECIPIENT_BUSINESS_NAME1", "RECIPIENT_BUSINESS_NAME2"],
    city: ["RECIPIENT_CITY"],
    state: ["RECIPIENT_STATE"],
    // Both spellings. The forms' own vocabulary is RECIPIENT_ZIP; RECIPIENT_ZIP_CODE is what
    // the upload column aliases produce and what customers write from the NEC template, and
    // a record carrying its ZIP under that name was passing every check here and then dying
    // in the send-side sanitizer with "no city/state/ZIP" (TS-8336).
    zip: ["RECIPIENT_ZIP", "RECIPIENT_ZIP_CODE"],
    foreignCountry: ["FOREIGN_COUNTRY", "RECIPIENT_COUNTRY"],
    taxYear: COMMON_YEAR,
    ...overrides,
  };
}

export const FORM_IDENTITY: Record<string, FormIdentityFields> = {
  "1099-NEC": recipientDefaults(),
  "1099-MISC": recipientDefaults(),
  "1099-INT": recipientDefaults(),
  "1099-DIV": recipientDefaults(),
  "1099-R": recipientDefaults(),
  "1099-C": recipientDefaults(),
  "1099-B": recipientDefaults({
    personName: ["RECIPIENT_FIRST_NAME", "RECIPIENT_LAST_NAME", "RECIPIENT_NAME1", "RECIPIENT_NAME2"],
  }),
  // 1099-Q uses the 2026 split-name layout under the default RECIPIENT_* names.
  "1099-Q": recipientDefaults(),
  "1099-SA": recipientDefaults({
    recipientTin: ["BOX_RECIPIENT_ID_NUMBER", "RECIPIENT_ID_NUMBER"],
  }),
  "5498-SA": recipientDefaults({
    recipientTin: ["BOX_RECIPIENT_ID_NUMBER", "RECIPIENT_ID_NUMBER"],
  }),
  "1099-K": recipientDefaults({
    recipientTin: ["PAYEE_TIN", "PAYEE_ID_NUMBER"],
    personName: ["PAYEE_NAME", "PAYEE_NAME2"],
    businessName: ["PAYEE_NAME", "PSE_NAME"],
    city: ["PAYEE_CITY"],
    state: ["PAYEE_STATE"],
    zip: ["PAYEE_ZIP"],
    foreignCountry: ["PAYEE_COUNTRY", "FOREIGN_COUNTRY"],
  }),
  "1099-LTC": recipientDefaults({
    filerTin: ["PAYERS_TIN", "FILER_FEDERAL_ID_NUMBER"],
    recipientTin: ["POLICYHOLDER_TAX_ID", "INSURED_SSN"],
    personName: ["POLICYHOLDER_NAME", "INSURED_NAME"],
    businessName: ["POLICYHOLDER_NAME"],
    city: ["POLICYHOLDER_CITY"],
    state: ["POLICYHOLDER_STATE"],
    zip: ["POLICYHOLDER_ZIP_CODE"],
    foreignCountry: ["FOREIGN_COUNTRY"],
  }),
  // The 2026 template's own names: LENDER_* files, BORROWER_* receives. The borrower's
  // business name is BORROWER_COMPANY_NAME. Its second line (the template's
  // RECIPIENT_BUSINESS_NAME2) is left out on purpose: the generator files BusinessNameLine1Txt
  // from BORROWER_COMPANY_NAME only, so a DBA line alone is not a name the IRS can take.
  // BORROWER_COUNTRY is the foreign country, read with FOREIGN_COUNTRY_INDICATOR exactly as
  // FOREIGN_COUNTRY is on the other forms.
  "1098": recipientDefaults({
    filerTin: ["LENDER_FEDERAL_ID_NUMBER"],
    recipientTin: ["BORROWER_ID_NUMBER"],
    personName: ["BORROWER_FIRST_NAME", "BORROWER_LAST_NAME"],
    businessName: ["BORROWER_COMPANY_NAME"],
    city: ["BORROWER_CITY"],
    state: ["BORROWER_STATE"],
    zip: ["BORROWER_ZIP"],
    foreignCountry: ["BORROWER_COUNTRY"],
  }),
  "1098-E": recipientDefaults({
    recipientTin: ["BORROWER_SSN", "RECIPIENT_ID_NUMBER"],
    personName: [
      "BORROWER_FIRST_NAME",
      "BORROWER_LAST_NAME",
      "BORROWER_NAME",
      "BORROWER_NAME2",
    ],
    businessName: ["BORROWER_BUSINESS_NAME1", "BORROWER_NAME"],
    city: ["BORROWER_CITY"],
    state: ["BORROWER_STATE"],
    zip: ["BORROWER_ZIP"],
  }),
  "1098-T": recipientDefaults({
    recipientTin: ["STUDENT_SSN", "RECIPIENT_ID_NUMBER"],
    personName: [
      "STUDENT_FIRST_NAME",
      "STUDENT_LAST_NAME",
      "STUDENT_NAME",
      "STUDENT_NAME2",
    ],
    businessName: ["STUDENT_BUSINESS_NAME1"],
    city: ["STUDENT_CITY"],
    state: ["STUDENT_STATE"],
    zip: ["STUDENT_ZIP"],
  }),
  "3921": recipientDefaults({
    recipientTin: ["EMPLOYEE_SSN", "RECIPIENT_ID_NUMBER"],
    personName: [
      "EMPLOYEE_FIRST_NAME",
      "EMPLOYEE_LAST_NAME",
      "EMPLOYEE_NAME",
      "EMPLOYEE_NAME2",
    ],
    businessName: ["EMPLOYEE_BUSINESS_NAME1", "EMPLOYEE_BUSINESS_NAME2"],
    city: ["EMPLOYEE_CITY"],
    state: ["EMPLOYEE_STATE"],
    zip: ["EMPLOYEE_ZIP"],
  }),
  "3922": recipientDefaults({
    filerTin: ["CORPORATION_EIN", "FILER_FEDERAL_ID_NUMBER"],
    recipientTin: ["EMPLOYEE_SSN", "RECIPIENT_ID_NUMBER"],
    personName: [
      "EMPLOYEE_FIRST_NAME",
      "EMPLOYEE_LAST_NAME",
      "EMPLOYEE_NAME",
      "EMPLOYEE_NAME2",
    ],
    businessName: ["EMPLOYEE_BUSINESS_NAME1", "EMPLOYEE_BUSINESS_NAME2"],
    city: ["EMPLOYEE_CITY"],
    state: ["EMPLOYEE_STATE"],
    zip: ["EMPLOYEE_ZIP"],
  }),
  "1042-S": recipientDefaults({
    filerTin: ["BOX12A_WITHHOLDING_AGENT_EIN", "FILER_FEDERAL_ID_NUMBER"],
    recipientTin: ["BOX13E_RECIPIENT_US_TIN", "BOX13I_RECIPIENTS_FOREIGN_TIN"],
    personName: ["BOX13A_RECIPIENT_NAME1"],
    businessName: ["BOX13A_RECIPIENT_NAME1"],
    city: ["BOX13D_RECIPIENT_CITY"],
    state: ["BOX13D_RECIPIENT_STATE"],
    zip: ["BOX13D_RECIPIENT_ZIP"],
    // 1042-S is the foreign-recipient form: a foreign address is expected, not a blocker.
    foreignCountry: [],
  }),
};

/** First non-empty value among the candidate columns. */
export function pick(record: Record<string, unknown>, fields: string[]): string {
  for (const f of fields) {
    const v = record?.[f];
    if (v !== undefined && v !== null && String(v).trim() !== "") return String(v).trim();
  }
  return "";
}

/** Which column supplied a value (for the issue's `field`), or the first candidate. */
export function pickField(record: Record<string, unknown>, fields: string[]): string | undefined {
  for (const f of fields) {
    const v = record?.[f];
    if (v !== undefined && v !== null && String(v).trim() !== "") return f;
  }
  return fields[0];
}

/**
 * Sibling spellings of the same column. The forms' vocabulary is the key; the value is the
 * spelling a record may arrive under instead - from the NEC upload template, from a hand-written
 * JSON body, from a customer who knows the other product. Kept short and explicit: this is not
 * fuzzy matching, it is the two or three names one column has actually been given.
 */
const SIBLING_SPELLINGS: Record<string, string[]> = {
  RECIPIENT_ZIP: ["RECIPIENT_ZIP_CODE"],
  FILER_ZIP: ["FILER_ZIP_CODE"],
};

/**
 * Moves each value that arrived under a sibling spelling onto the spelling this record's form
 * actually reads, when that key is empty. The record is returned as a new object; the original
 * is not touched. A record already written in the form's own vocabulary comes back unchanged.
 *
 * Applied where records enter - the JSON body of POST /v1/e-files and each spreadsheet row -
 * so the checks, the stored record and the generator all see one name for one column. Without
 * it a 1099-MISC pasted with RECIPIENT_ZIP_CODE passed every intake check (the ZIP was only
 * validated when present under RECIPIENT_ZIP) and then threw in the send-side sanitizer.
 */
export function canonicalizeRecordKeys(record: Record<string, unknown>): Record<string, unknown> {
  let out: Record<string, unknown> | null = null;
  for (const [canonical, siblings] of Object.entries(SIBLING_SPELLINGS)) {
    const own = record?.[canonical];
    const ownEmpty = own === undefined || own === null || String(own).trim() === "";
    if (!ownEmpty) continue;
    for (const sibling of siblings) {
      const value = record?.[sibling];
      if (value === undefined || value === null || String(value).trim() === "") continue;
      out ??= { ...record };
      out[canonical] = value;
      delete out[sibling];
      break;
    }
  }
  return out ?? record;
}
